<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { t } from '../i18n';
import {
  fetchCatalog, buyFromCatalog, quickSellArtifact,
  fetchSellQuote, marketSellArtifact, fetchPending,
  type MarketCurrency, type CatalogItem, type SellQuote, type PendingSaleEntry,
} from '../game/market-api';
import {
  fetchShop, buyShopItem,
  type ShopItem, type ShopGroup, type ShopState, type SlotTerms,
} from '../game/shop-api';
import {
  fetchInventory, SLOTS,
  type Inventory as GearInventory, type Artifact, type ArtifactRarity, type ArtifactType, type EquipSlot,
} from '../game/inventory-api';
import { RARITY_HEX, SLOT_LABELS, scaledStats, STAT_META } from '../game/item-math';
import { BG, fightPose, itemThumb, shopArt, UI } from '../game/art';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import ItemModal from './ItemModal.vue';
import ChestReveal from './ChestReveal.vue';
import SwipeDeck from './SwipeDeck.vue';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';

/**
 * "THE QUARTER" — the flagship commerce screen. NPC storefront (The Rack),
 * consumables/boosts (Supplies & Favors), and instant quick-sell (Sell),
 * plus the Counting House (gold bars → cash exchange). One data fetch
 * (refreshAll) feeds every tab so switching tabs never re-hits the network.
 */
type Tab = 'rack' | 'supplies' | 'sell';

// The trader standing in the stall front — presentational only, no game state.
const vendorArt = fightPose('kingpin');

const props = defineProps<{ initialTab?: Tab; spotlightItem?: string | null }>();
const emit = defineEmits<{ (e: 'updated'): void; (e: 'topup'): void; (e: 'counting'): void }>();

const tab = ref<Tab>(props.initialTab ?? 'rack');

const loading = ref(true);
const error = ref('');
const notice = ref('');
const busy = ref<string | null>(null);

// ── Shared data (one fetch feeds every tab) ─────────────────────────────
const inventory = ref<GearInventory | null>(null);
const npcCatalog = ref<CatalogItem[]>([]);
const shopCatalog = ref<ShopItem[]>([]);
const balance = ref<{ soft: number; premium: number }>({ soft: 0, premium: 0 });
const userLevel = ref<number>(1);
const shopState = ref<ShopState | null>(null);
const slots = ref<SlotTerms | null>(null);
const slotsMaxed = computed(() => slots.value != null && slots.value.nextPrice == null);

const bag = computed<Artifact[]>(() => inventory.value?.bag ?? []);

async function refreshAll() {
  error.value = '';
  try {
    const [inv, shop, rack] = await Promise.all([
      fetchInventory(), fetchShop(), fetchCatalog(),
    ]);
    inventory.value = inv;
    shopCatalog.value = shop.catalog;
    balance.value = shop.balance;
    userLevel.value = shop.level;
    shopState.value = shop.state;
    slots.value = shop.slots;
    npcCatalog.value = rack;
  } catch (e) {
    error.value = (e as Error).message;
  }
}

onMounted(async () => {
  await refreshAll();
  loading.value = false;
  await loadPending();
  pendingTimer = window.setInterval(() => { nowTick.value = Date.now(); }, 30_000);
});
onUnmounted(() => { if (pendingTimer) window.clearInterval(pendingTimer); });

function slotOf(type: ArtifactType): EquipSlot {
  return type.toLowerCase() as EquipSlot;
}

// ── THE RACK — NPC catalog (cash) + Vault (gold bars) unified in one grid ──
type SaleCurrency = 'SOFT' | 'PREMIUM' | 'BOTH';
interface RackCard {
  id: string;
  source: 'catalog' | 'vault';
  name: string;
  rarity: ArtifactRarity;
  type: ArtifactType;
  attack: number; defense: number; magic: number; speed: number; hp: number;
  /** The piece's GRADE, not a sharpening step — a Vault ring is a tier-6 item.
   *  Rack catalogue pieces are all tier 1. */
  level: number;
  setName: string | null;
  statusEffect: string | null;
  description: string | null;
  price: number;
  currency: MarketCurrency;
  minLevel: number;
  /** What runes buy this for — 0 when it cannot be bought with runes at all. */
  runePrice: number;
  /** Show BOTH price rows and both buy buttons. True for anything with a silver
   *  price, since dominion's alt-buy makes every such item payable in runes too
   *  (QA 86cb9yd3p item 5) — which is how epic and legendary got their rune
   *  button. False for runes-only Apex gear and for Vault items. */
  dualPriced: boolean;
  saleCurrency: SaleCurrency;
}

function catalogToCard(c: CatalogItem): RackCard {
  // Runes-only (Apex) gear has no silver price at all; everything else leads
  // with silver and offers runes alongside it.
  const runesOnly = c.saleCurrency === 'PREMIUM';
  const currency: MarketCurrency = runesOnly ? 'PREMIUM' : 'SOFT';
  return {
    id: c.id, source: 'catalog', name: c.name,
    rarity: c.rarity as ArtifactRarity, type: c.type as ArtifactType,
    attack: c.attack, defense: c.defense, magic: c.magic, speed: c.speed, hp: c.hp,
    level: 1, setName: c.setName, statusEffect: c.statusEffect, description: null,
    price: runesOnly ? c.runePrice : c.basePrice,
    currency,
    minLevel: c.minLevel,
    runePrice: c.runePrice,
    dualPriced: !runesOnly && c.basePrice > 0 && c.runePrice > 0,
    saleCurrency: c.saleCurrency,
  };
}
function vaultToCard(i: ShopItem): RackCard {
  const v = i.vault!; // vaultItems filter guarantees the spec is present
  return {
    id: i.id, source: 'vault', name: v.name ?? i.name,
    rarity: v.rarity, type: v.type,
    attack: v.attack, defense: v.defense, magic: v.magic, speed: v.speed, hp: v.hp,
    level: v.level, setName: v.setName ?? null, statusEffect: v.statusEffect ?? null, description: i.description,
    price: i.price, currency: i.currency,
    // Vault items are the pre-existing single-price mechanic — untouched by
    // this ticket, so they always resolve to a single-currency sale.
    minLevel: 1,
    runePrice: i.currency === 'PREMIUM' ? i.price : 0,
    dualPriced: false,
    saleCurrency: i.currency,
  };
}

/** Only REAL gear belongs in the Rack — the Golden Chest (gacha) and Stash
 *  Expansion share the 'vault' group but have their own cards in Supplies. */
const vaultItems = computed(() =>
  shopCatalog.value.filter((i) => i.kind === 'vault_artifact' && i.vault));
const rackCards = computed<RackCard[]>(() => [
  ...npcCatalog.value.map(catalogToCard),
  ...vaultItems.value.map(vaultToCard),
]);

const RACK_SLOTS: (EquipSlot | 'ALL')[] = ['ALL', ...SLOTS];
const RACK_RARITIES: (ArtifactRarity | 'ALL')[] = ['ALL', 'COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY', 'MYTHIC'];
type RackCurrencyFilter = 'ALL' | 'SOFT' | 'PREMIUM';
const RACK_CURRENCIES: RackCurrencyFilter[] = ['ALL', 'SOFT', 'PREMIUM'];
const rackSlot = ref<EquipSlot | 'ALL'>('ALL');
const rackRarity = ref<ArtifactRarity | 'ALL'>('ALL');
const rackCurrency = ref<RackCurrencyFilter>('ALL');
const rackInspect = ref<RackCard | null>(null);

const filteredRack = computed(() => rackCards.value.filter((c) =>
  (rackSlot.value === 'ALL' || slotOf(c.type) === rackSlot.value) &&
  (rackRarity.value === 'ALL' || c.rarity === rackRarity.value) &&
  // Filter on what the card can actually be PAID with, not on what the def
  // called it — alt-buy means most silver items now answer the rune filter too.
  (rackCurrency.value === 'ALL'
    || (rackCurrency.value === 'SOFT' && c.currency === 'SOFT')
    || (rackCurrency.value === 'PREMIUM' && c.runePrice > 0)),
));

// Presentational split — the best 3 pieces get the shrine shelf, everything
// else stays in the tray. Purely a display grouping; filteredRack is unchanged.
const featured = computed(() => filteredRack.value.filter((c) =>
  c.rarity === 'LEGENDARY' || c.rarity === 'EPIC' || c.rarity === 'MYTHIC').slice(0, 3));
const trayCards = computed(() => filteredRack.value.filter((c) => !featured.value.includes(c)));

function rarityLabel(r: ArtifactRarity): string {
  return t(`market.rarity.${r.toLowerCase()}`);
}

function rackAfford(c: RackCard, currency: MarketCurrency = c.currency): boolean {
  if (currency === 'PREMIUM') return balance.value.premium >= c.runePrice;
  return balance.value.soft >= c.price;
}
/** Preemptive block — mirrors the backend's "Stash full" 400 before the player even tries. */
function rackBlocked(_c: RackCard): string | null {
  if (inventory.value && inventory.value.bagCount >= inventory.value.bagCap) return t('market.blocked.chestFull');
  return null;
}
/** Account-level gate preview — mirrors the server-side check in
 *  MarketService.buyFromCatalog (item-expansion-14-08.md §3.4/§5.4). */
function rackLevelLocked(c: RackCard): boolean {
  return userLevel.value < c.minLevel;
}
function rackDisabled(c: RackCard, currency: MarketCurrency = c.currency): string | null {
  if (rackLevelLocked(c)) return t('market.blocked.levelLocked', { level: c.minLevel });
  return rackBlocked(c) ?? (!rackAfford(c, currency) ? t('market.blocked.notEnough') : null);
}
function rackModalBlocked(c: RackCard): string | null {
  return rackDisabled(c);
}
function priceLabel(price: number, currency: MarketCurrency): string {
  return `${price.toLocaleString()} ${currency === 'PREMIUM' ? t('shop.runesUnit') : t('common.silver')}`;
}

function cardToArtifact(c: RackCard): Artifact {
  return {
    id: c.id, name: c.name, description: c.description,
    rarity: c.rarity, type: c.type,
    attack: c.attack, defense: c.defense, magic: c.magic, speed: c.speed, hp: c.hp,
    // The card's level is the piece's grade, so it lands on powerLevel; a bought
    // piece always arrives with its full 1..5 sharpening track ahead of it.
    powerLevel: c.level, level: 1, setName: c.setName, statusEffect: c.statusEffect,
    // catalog cards carry their asking price; the sell-payout preview never
    // renders in buy mode, so mirroring price into both fields is safe
    currentPrice: c.price, basePrice: c.price,
    imageUrl: null, isBreakable: false, maxDurability: null, durability: null, isBroken: false,
  };
}
function equippedFor(c: RackCard): Artifact | null {
  return inventory.value?.equipped[slotOf(c.type)] ?? null;
}

async function onBuyRack(c: RackCard, currency: MarketCurrency = c.currency) {
  const price = currency === 'PREMIUM' ? c.runePrice : c.price;
  const ok = await confirmAction({
    kicker: t('market.confirm.tradestead'),
    title: `${t('market.buy')} ${c.name}?`,
    icon: itemThumb(c.type, c.rarity, c.name),
    rows: [
      { label: t('market.rows.piece'), value: `${rarityLabel(c.rarity)} ${SLOT_LABELS[slotOf(c.type)]} · ${t('items.modal.tier', { tier: c.level })}`, tone: 'dim' },
      { label: t('market.rows.price'), value: price.toLocaleString(), icon: currency === 'PREMIUM' ? runesIcon : cashIcon, tone: currency === 'PREMIUM' ? 'bright' : 'gold' },
    ],
    confirmLabel: t('market.buyIt'),
  });
  if (!ok) return;
  busy.value = c.id; error.value = ''; notice.value = '';
  try {
    if (c.source === 'catalog') {
      const res = await buyFromCatalog(c.id, currency);
      toast(t('market.toast.bought', { name: res.name }), { icon: itemThumb(c.type, c.rarity, c.name), accent: RARITY_HEX[c.rarity] });
    } else {
      const res = await buyShopItem(c.id);
      toast(res.granted?.name ? t('market.toast.acquired', { name: res.granted.name }) : t('market.toast.bought', { name: res.name }), { icon: itemThumb(c.type, c.rarity, c.name), accent: RARITY_HEX[c.rarity] });
    }
    rackInspect.value = null;
    await refreshAll();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

function statLine(a: Artifact): string {
  const parts: string[] = [];
  if (a.attack) parts.push(`DMG ${a.attack}`);
  if (a.defense) parts.push(`TUF ${a.defense}`);
  if (a.magic) parts.push(`CUN ${a.magic}`);
  if (a.speed) parts.push(`RFX ${a.speed}`);
  if (a.hp) parts.push(`HP ${a.hp}`);
  return parts.length ? parts.join(' · ') : t('market.noBonuses');
}

// ── SUPPLIES & FAVORS — shop consumables / boosts / chest / expansion ───
const GROUPS: { key: ShopGroup; title: string; blurb: string }[] = [
  { key: 'consumable', title: 'Supplies', blurb: 'Pay in runes — patch up and steady your fury.' },
  { key: 'boost', title: 'Favors', blurb: 'Pay in runes — leverage that pays off.' },
];
const SPECIAL_KINDS = new Set(['chest_pull', 'inventory_slot']);
function itemsOf(group: ShopGroup): ShopItem[] {
  return shopCatalog.value.filter((i) => i.group === group && !SPECIAL_KINDS.has(i.kind));
}
const chestItem = computed(() => shopCatalog.value.find((i) => i.kind === 'chest_pull') ?? null);
const expansionItem = computed(() => shopCatalog.value.find((i) => i.kind === 'inventory_slot') ?? null);

function durationLabel(ms: number | null): string {
  if (!ms) return '';
  const mins = Math.round(ms / 60000);
  return mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}m`;
}
function magnitudeLabel(i: ShopItem): string | null {
  if (i.kind === 'hp_partial' && i.magnitude != null) return `+${Math.round(i.magnitude * 100)}% health`;
  if (i.kind === 'hp_fixed' && i.amount != null) return `+${i.amount} health`;
  return null;
}
function affordable(i: ShopItem): boolean {
  return i.currency === 'PREMIUM' ? balance.value.premium >= i.price : balance.value.soft >= i.price;
}
function blockedReason(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s) return null;
  if (i.kind === 'hp_refill' || i.kind === 'hp_partial' || i.kind === 'hp_fixed') { if (s.hp >= s.maxHp) return t('market.blocked.fullHealth'); }
  if (i.kind === 'nerve_refill') {
    if (s.nerve >= s.maxNerve) return t('market.blocked.fullFury');
    if (i.dailyLimit != null && s.nerveRefillsUsedToday >= i.dailyLimit) return t('market.blocked.dailyLimit');
  }
  if (i.kind === 'revive' && s.hasRevive) return t('market.blocked.alreadyHeld');
  if ((i.kind === 'vault_artifact' || i.kind === 'chest_pull') && s.bagCount >= s.bagCap) return t('market.blocked.chestFull');
  return null;
}
function activeHint(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s) return null;
  if (i.kind === 'xp_booster' && s.xpBoosterUntil) return t('market.blocked.active');
  if (i.kind === 'cash_rush' && s.cashRushUntil) return t('market.blocked.active');
  return null;
}
function dailyCapLabel(i: ShopItem): string | null {
  const s = shopState.value;
  if (!s || i.kind !== 'nerve_refill' || i.dailyLimit == null) return null;
  return `×${s.nerveRefillsUsedToday}/${i.dailyLimit} today`;
}

async function onBuySupply(i: ShopItem) {
  const chest = i.kind === 'chest_pull';
  const ok = await confirmAction({
    kicker: chest ? t('market.confirm.wyrmChest') : t('market.confirm.supplies'),
    title: chest ? `${t('market.pull')} ${t('market.wyrmChest.title')}?` : `${t('market.buy')} ${i.name}?`,
    icon: shopArt(i.kind) || undefined,
    rows: [
      { label: t('market.rows.price'), value: i.price.toLocaleString(), icon: i.currency === 'PREMIUM' ? runesIcon : cashIcon, tone: i.currency === 'PREMIUM' ? 'bright' : 'gold' },
    ],
    note: i.description ?? undefined,
    confirmLabel: chest ? t('market.pull') : t('market.buyIt'),
  });
  if (!ok) return;
  busy.value = i.id; error.value = ''; notice.value = '';
  try {
    const res = await buyShopItem(i.id);
    await refreshAll();
    emit('updated');
    if (chest && res.granted) {
      // The Golden Chest gets a cinematic reveal instead of a plain toast —
      // pull the full stats from the freshly-refreshed bag for the card.
      const full = inventory.value?.bag.find((a) => a.id === res.granted!.id) ?? null;
      chestReward.value = full ?? {
        id: res.granted.id, name: res.granted.name, description: null,
        rarity: res.granted.rarity, type: res.granted.type,
        attack: 0, defense: 0, magic: 0, speed: 0, hp: 0, level: 1,
        setName: null, statusEffect: null, currentPrice: 0, basePrice: 0,
        imageUrl: null, isBreakable: false, maxDurability: null, durability: null, isBroken: false,
      };
    } else {
      toast(res.granted?.name ? t('market.toast.acquired', { name: res.granted.name }) : t('market.toast.bought', { name: res.name }), { icon: shopArt(i.kind) });
    }
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

// The Golden Chest reveal overlay — set to the won piece after a pull.
const chestReward = ref<Artifact | null>(null);

// ── SELL — pick from the bag; quick-sell now (50% silver / 70% runes) or fence it (90%, 12h lock) ──
/** Coin face for a payout — gear is sold in ITS OWN currency, never converted. */
const payIcon = (currency: MarketCurrency) => (currency === 'PREMIUM' ? runesIcon : cashIcon);
const sellTarget = ref<Artifact | null>(null);
const sellQuote = ref<SellQuote | null>(null);
const quoteLoading = ref(false);

async function pickToSell(a: Artifact) {
  sellTarget.value = a;
  sellQuote.value = null;
  quoteLoading.value = true;
  try { sellQuote.value = await fetchSellQuote(a.id); }
  catch (e) { error.value = (e as Error).message; }
  finally { quoteLoading.value = false; }
}

async function onQuicksell() {
  const a = sellTarget.value; const q = sellQuote.value;
  if (!a || !q) return;
  const ok = await confirmAction({
    kicker: t('market.confirm.quickSell'),
    title: `${t('market.quickSell')} ${a.name}?`,
    icon: itemThumb(a.type, a.rarity, a.name),
    rows: [
      { label: t('market.rows.streetValue'), value: q.gross.toLocaleString(), icon: payIcon(q.currency), tone: 'dim' },
      { label: t('market.losePct', { pct: 100 - q.quick.pct }), value: `−${(q.gross - q.quick.payout).toLocaleString()}`, tone: 'error' },
      { label: t('market.rows.youGetNow'), value: q.quick.payout.toLocaleString(), icon: payIcon(q.currency), tone: 'success' },
    ],
    note: t('market.sellNote'),
    confirmLabel: t('market.takeAmount', { amount: q.quick.payout.toLocaleString() }),
    danger: true,
  });
  if (!ok) return;
  busy.value = 'quicksell'; error.value = '';
  try {
    const res = await quickSellArtifact(a.id);
    toast(t('market.toast.quickSold', { amount: res.value.toLocaleString() }), { icon: payIcon(res.currency) });
    sellTarget.value = null; sellQuote.value = null;
    await refreshAll(); await loadPending();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function onMarketSell() {
  const a = sellTarget.value; const q = sellQuote.value;
  if (!a || !q) return;
  const ok = await confirmAction({
    kicker: t('market.confirm.broker'),
    title: `${t('market.brokerIt')} ${a.name}?`,
    icon: itemThumb(a.type, a.rarity, a.name),
    rows: [
      { label: t('market.rows.streetValue'), value: q.gross.toLocaleString(), icon: payIcon(q.currency), tone: 'dim' },
      { label: t('market.brokersCut', { pct: 100 - q.market.pct }), value: `−${(q.gross - q.market.payout).toLocaleString()}`, tone: 'error' },
      { label: t('market.payoutIn', { hours: q.market.lockHours }), value: q.market.payout.toLocaleString(), icon: payIcon(q.currency), tone: 'success' },
    ],
    note: t('market.brokerLockNote', { hours: q.market.lockHours }),
    confirmLabel: t('market.brokerFor', { amount: q.market.payout.toLocaleString() }),
  });
  if (!ok) return;
  busy.value = 'marketsell'; error.value = '';
  try {
    const res = await marketSellArtifact(a.id);
    toast(t('market.toast.brokerOut', { amount: res.payout.toLocaleString(), time: `${q.market.lockHours}h` }), { icon: payIcon(res.currency) });
    sellTarget.value = null; sellQuote.value = null;
    await refreshAll(); await loadPending();
    emit('updated');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

// ── At the fence — locked payouts; the server credits any that matured on fetch ──
const pending = ref<PendingSaleEntry[]>([]);
const nowTick = ref(Date.now());
let pendingTimer = 0;

async function loadPending() {
  try {
    const v = await fetchPending();
    pending.value = v.pending;
    if (v.collected > 0) {
      toast(t('market.toast.brokerCleared', { amount: v.collected.toLocaleString() }), { icon: cashIcon });
    }
    // Rune-priced gear clears as runes — its own toast, its own coin.
    if (v.collectedPremium > 0) {
      toast(t('market.toast.brokerCleared', { amount: v.collectedPremium.toLocaleString() }), { icon: runesIcon });
    }
    if (v.collected > 0 || v.collectedPremium > 0) {
      await refreshAll();
      emit('updated');
    }
  } catch { /* non-fatal — the fence list just stays as-is */ }
}

/** Time left on a locked payout, e.g. "3h 12m". */
function untilLabel(matureAt: string): string {
  const ms = new Date(matureAt).getTime() - nowTick.value;
  if (ms <= 0) return 'clearing…';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// The Counting House (bars → cash) is its own full screen now — see
// CountingHouse.vue; the wallet-strip button just asks HeroScreen to open it.
</script>

<template>
  <section class="tr-page">
    <!-- THE STALL FRONT — a scene, not a banner. The trader stands in it. -->
    <div class="tr-front">
      <div class="tr-front-scene">
        <img :src="UI.marketBeam" alt="" class="tr-front-bg" />
        <div class="tr-front-shade" aria-hidden="true"></div>

        <img :src="vendorArt" alt="" class="tr-vendor" />

        <div class="tr-front-title">
          <h1 class="gilt-text tr-front-h1">{{ t('market.title') }}</h1>
          <p class="tr-front-sub">{{ t('market.sub') }}</p>
        </div>

        <!-- THE COUNTER — balances chiselled into a small iron ledge -->
        <div class="tr-purse">
          <div class="chip-rune">
            <img :src="cashIcon" class="w-4 h-4 shrink-0" alt="" />
            <span class="font-heading normal-case text-gold text-sm leading-none">{{ balance.soft.toLocaleString() }}</span>
            <span>{{ t('common.silver') }}</span>
          </div>
          <div class="chip-rune">
            <img :src="runesIcon" class="w-4 h-4 shrink-0" alt="" />
            <span class="font-heading normal-case text-gold-bright text-sm leading-none">{{ balance.premium.toLocaleString() }}</span>
            <span>{{ t('shop.runesUnit') }}</span>
          </div>
          <button class="btn-forge btn-forge--carved btn-forge--sm" @click="emit('topup')">+ {{ t('market.buy') }} {{ t('shop.runesUnit') }}</button>
          <button class="btn-forge btn-forge--rune btn-forge--sm max-w-full" @click="emit('counting')">
            <span class="sm:hidden">{{ t('market.tabs.weighing') }}</span>
            <span class="hidden sm:inline">{{ t('market.tabs.weighingFull') }}</span>
          </button>
        </div>
      </div>

      <!-- TABS — hanging shingles under the stall front's edge -->
      <nav class="tr-shingle-rail">
        <button v-for="tb in (['rack', 'supplies', 'sell'] as const)" :key="tb" @click="tab = tb"
          :class="['tr-shingle', tab === tb && 'tr-shingle-active']">
          <span class="tr-shingle-face" aria-hidden="true"></span>
          <span class="tr-shingle-txt">
            {{ tb === 'rack' ? t('market.tabs.rack') : tb === 'supplies' ? t('market.tabs.supplies') : t('market.tabs.sell') }}
            <span v-if="tb === 'rack'" class="mkt-count">{{ rackCards.length }}</span>
            <span v-else-if="tb === 'sell'" class="mkt-count">{{ bag.length }}</span>
          </span>
        </button>
      </nav>
    </div>

    <p v-if="loading" class="text-text-dim">{{ t('common.loading') }}</p>
    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
    <p v-if="notice" class="mb-4 text-sm text-success">{{ notice }}</p>

    <template v-if="!loading">
      <!-- ═══ THE RACK ═══════════════════════════════════════════════ -->
      <div v-if="tab === 'rack'">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="tr-filter-label">{{ t('market.filters.slot') }}</span>
          <button v-for="s in RACK_SLOTS" :key="'rs-' + s" @click="rackSlot = s"
            :class="['chip-rune', rackSlot === s && 'chip-rune-active']">{{ s === 'ALL' ? t('market.filters.all') : SLOT_LABELS[s] }}</button>
        </div>
        <div class="flex flex-wrap items-center gap-2 mb-5">
          <span class="tr-filter-label">{{ t('market.filters.rarity') }}</span>
          <button v-for="r in RACK_RARITIES" :key="'rr-' + r" @click="rackRarity = r"
            :class="['chip-rune', rackRarity === r && 'chip-rune-active']">
            <span v-if="r !== 'ALL'" class="mkt-dot" :style="{ background: RARITY_HEX[r] }"></span>
            {{ r === 'ALL' ? t('market.filters.all') : rarityLabel(r) }}
          </button>
        </div>
        <div class="flex flex-wrap items-center gap-2 mb-5">
          <span class="tr-filter-label">{{ t('market.filters.currency') }}</span>
          <button v-for="c in RACK_CURRENCIES" :key="'rc-' + c" @click="rackCurrency = c"
            :class="['chip-rune', rackCurrency === c && 'chip-rune-active']">
            <img v-if="c === 'PREMIUM'" :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
            <img v-else-if="c === 'SOFT'" :src="cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
            {{ c === 'ALL' ? t('market.filters.all') : c === 'SOFT' ? t('market.filters.silver') : t('market.filters.runes') }}
          </button>
        </div>

        <p v-if="filteredRack.length === 0" class="font-body text-text-dim text-sm">{{ t('market.empty.noMatch') }}</p>
        <template v-else>
          <!-- THE SHRINE — the best pieces, on a shelf of their own.
               Runs through SwipeDeck so its mobile carousel and dots are the
               SAME control as the tray's below, rather than a look-alike (the
               deck's styles are scoped, so a hand-rolled copy would drift).
               mobile-cols 1 + page-size 1 → one hero card per swipe, no peek.
               auto-fit keeps three cards stretching across the desktop shelf;
               auto-fill would leave empty tracks and pin them to 200px. -->
          <div v-if="featured.length" class="tr-shrine">
            <SwipeDeck
              :items="featured" :page-size="1" :mobile-cols="1" auto-fit
              min-col="200px" gap="16px" :item-key="(c: any) => 'feat-' + c.source + c.id"
            >
            <template #default="{ item: c }">
            <div class="tr-good">
              <button
                class="relic relic--lg"
                :class="{ 'relic--legend': c.rarity === 'LEGENDARY' }"
                :style="{ '--rar': RARITY_HEX[c.rarity] }"
                :title="`${c.name} — details`"
                @click="rackInspect = c"
              >
                <span class="relic-cord" aria-hidden="true"></span>
                <span class="relic-lv">{{ t('market.lv', { level: c.level }) }}</span>
                <img :src="itemThumb(c.type, c.rarity, c.name)" :alt="c.type" class="relic-art" loading="lazy" decoding="async" />
                <span class="relic-name">{{ c.name }}</span>
                <span class="relic-meta">
                  {{ SLOT_LABELS[slotOf(c.type)] }}<template v-if="c.setName"> · {{ c.setName }}</template>
                  <template v-if="c.source === 'vault'"> · Hoard</template>
                  <span class="tr-specs">
                    <template v-for="(v, k) in scaledStats(cardToArtifact(c))" :key="k">
                      <span v-if="v !== 0" class="tr-spec"><b>{{ v }}</b><i>{{ k === 'hp' ? 'HP' : STAT_META[k as 'attack'].short }}</i></span>
                    </template>
                  </span>
                </span>
              </button>
              <!-- Dual-currency: ONE compact row while level-locked (two identical
                   REQUIRES LEVEL bars read as a rendering bug — owner screenshot),
                   two buy rows only once the gate is open. -->
              <div v-if="c.dualPriced && rackLevelLocked(c)" class="tr-price-row">
                <span class="tr-price tr-price--bright">
                  <img :src="cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.price.toLocaleString() }}
                </span>
                <span class="tr-price">
                  <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.runePrice.toLocaleString() }}
                </span>
                <button
                  class="btn-forge btn-forge--rune btn-forge--sm"
                  disabled :title="rackDisabled(c) ?? undefined"
                >{{ rackDisabled(c) }}</button>
              </div>
              <div v-else-if="c.dualPriced" class="flex flex-col gap-1.5">
                <div class="tr-price-row">
                  <span class="tr-price tr-price--bright">
                    <img :src="cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                    {{ c.price.toLocaleString() }}
                  </span>
                  <button
                    class="btn-forge btn-forge--rune btn-forge--sm"
                    :disabled="busy === c.id || !!rackDisabled(c, 'SOFT')"
                    :title="rackDisabled(c, 'SOFT') ?? `${t('market.buy')} ${c.name}`"
                    @click="onBuyRack(c, 'SOFT')"
                  >{{ busy === c.id ? '…' : (rackDisabled(c, 'SOFT') ?? t('market.buy')) }}</button>
                </div>
                <div class="tr-price-row">
                  <span class="tr-price tr-price--bright">
                    <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                    {{ c.runePrice.toLocaleString() }}
                  </span>
                  <button
                    class="btn-forge btn-forge--rune btn-forge--sm"
                    :disabled="busy === c.id || !!rackDisabled(c, 'PREMIUM')"
                    :title="rackDisabled(c, 'PREMIUM') ?? `${t('market.buy')} ${c.name}`"
                    @click="onBuyRack(c, 'PREMIUM')"
                  >{{ busy === c.id ? '…' : (rackDisabled(c, 'PREMIUM') ?? t('market.buy')) }}</button>
                </div>
              </div>
              <div v-else class="tr-price-row">
                <span class="tr-price tr-price--bright">
                  <img :src="c.currency === 'PREMIUM' ? runesIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.price.toLocaleString() }}
                </span>
                <button
                  class="btn-forge btn-forge--rune btn-forge--sm"
                  :disabled="busy === c.id || !!rackDisabled(c)"
                  :title="rackDisabled(c) ?? `${t('market.buy')} ${c.name}`"
                  @click="onBuyRack(c)"
                >{{ busy === c.id ? '…' : (rackDisabled(c) ?? t('market.buy')) }}</button>
              </div>
            </div>
            </template>
            </SwipeDeck>
          </div>

          <!-- THE TRAY — everything else, on the swipeable deck -->
          <div class="mkt-stall" :style="{ backgroundImage: `url(${UI.weaponRail})` }">
          <!-- row-step 5: the rack is the one deck long enough to need it (the
               full artifact catalogue). Desktop shows 5 rows and loads 5 more on
               demand; on a phone the deck's own swipe carousel still handles it.
               Deliberately NOT applied to the Sell deck below — that lists only
               what the player is carrying. -->
          <SwipeDeck
            :items="trayCards" :page-size="6" :row-step="5"
            min-col="160px" gap="0.85rem" :item-key="(c: any) => c.source + c.id"
          >
            <template #default="{ item: c }">
            <div class="tr-good">
              <button
                class="relic"
                :class="{ 'relic--legend': c.rarity === 'LEGENDARY' }"
                :style="{ '--rar': RARITY_HEX[c.rarity] }"
                :title="`${c.name} — details`"
                @click="rackInspect = c"
              >
                <span class="relic-cord" aria-hidden="true"></span>
                <span class="relic-lv">{{ t('market.lv', { level: c.level }) }}</span>
                <img :src="itemThumb(c.type, c.rarity, c.name)" :alt="c.type" class="relic-art" loading="lazy" decoding="async" />
                <span class="relic-name">{{ c.name }}</span>
                <span class="relic-meta">
                  {{ SLOT_LABELS[slotOf(c.type)] }}<template v-if="c.setName"> · {{ c.setName }}</template>
                  <template v-if="c.source === 'vault'"> · Hoard</template>
                  <template v-if="c.statusEffect"> · <i class="tr-fx">{{ c.statusEffect }}</i></template>
                  <span class="tr-specs">
                    <template v-for="(v, k) in scaledStats(cardToArtifact(c))" :key="k">
                      <span v-if="v !== 0" class="tr-spec"><b>{{ v }}</b><i>{{ k === 'hp' ? 'HP' : STAT_META[k as 'attack'].short }}</i></span>
                    </template>
                  </span>
                </span>
              </button>
              <!-- Dual-currency: ONE compact row while level-locked (two identical
                   REQUIRES LEVEL bars read as a rendering bug — owner screenshot),
                   two buy rows only once the gate is open. -->
              <div v-if="c.dualPriced && rackLevelLocked(c)" class="tr-price-row">
                <span class="tr-price tr-price--bright">
                  <img :src="cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.price.toLocaleString() }}
                </span>
                <span class="tr-price">
                  <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.runePrice.toLocaleString() }}
                </span>
                <button
                  class="btn-forge btn-forge--rune btn-forge--sm"
                  disabled :title="rackDisabled(c) ?? undefined"
                >{{ rackDisabled(c) }}</button>
              </div>
              <div v-else-if="c.dualPriced" class="flex flex-col gap-1.5">
                <div class="tr-price-row">
                  <span class="tr-price tr-price--bright">
                    <img :src="cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                    {{ c.price.toLocaleString() }}
                  </span>
                  <button
                    class="btn-forge btn-forge--rune btn-forge--sm"
                    :disabled="busy === c.id || !!rackDisabled(c, 'SOFT')"
                    :title="rackDisabled(c, 'SOFT') ?? `${t('market.buy')} ${c.name}`"
                    @click="onBuyRack(c, 'SOFT')"
                  >{{ busy === c.id ? '…' : (rackDisabled(c, 'SOFT') ?? t('market.buy')) }}</button>
                </div>
                <div class="tr-price-row">
                  <span class="tr-price tr-price--bright">
                    <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                    {{ c.runePrice.toLocaleString() }}
                  </span>
                  <button
                    class="btn-forge btn-forge--rune btn-forge--sm"
                    :disabled="busy === c.id || !!rackDisabled(c, 'PREMIUM')"
                    :title="rackDisabled(c, 'PREMIUM') ?? `${t('market.buy')} ${c.name}`"
                    @click="onBuyRack(c, 'PREMIUM')"
                  >{{ busy === c.id ? '…' : (rackDisabled(c, 'PREMIUM') ?? t('market.buy')) }}</button>
                </div>
              </div>
              <div v-else class="tr-price-row">
                <span class="tr-price tr-price--bright">
                  <img :src="c.currency === 'PREMIUM' ? runesIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ c.price.toLocaleString() }}
                </span>
                <button
                  class="btn-forge btn-forge--rune btn-forge--sm"
                  :disabled="busy === c.id || !!rackDisabled(c)"
                  :title="rackDisabled(c) ?? `${t('market.buy')} ${c.name}`"
                  @click="onBuyRack(c)"
                >{{ busy === c.id ? '…' : (rackDisabled(c) ?? t('market.buy')) }}</button>
              </div>
            </div>
            </template>
          </SwipeDeck>
          </div>
        </template>
      </div>

      <!-- ═══ SUPPLIES & FAVORS ══════════════════════════════════════ -->
      <div v-else-if="tab === 'supplies'">
        <div v-for="g in GROUPS" :key="g.key" class="mb-8">
          <h2 class="font-heading text-lg text-gold">{{ g.title }}</h2>
          <p class="font-body text-xs text-text-dim mb-3">{{ g.blurb }}</p>

          <p v-if="itemsOf(g.key).length === 0" class="font-body text-text-dim text-sm">{{ t('market.empty.nothingHere') }}</p>
          <div v-else class="tr-shelf">
            <div class="tr-jars">
              <div v-for="i in itemsOf(g.key)" :key="i.id" class="tr-jar" :class="{ 'guide-glow': props.spotlightItem === i.id }">
                <img v-if="shopArt(i.kind)" :src="shopArt(i.kind)" :alt="i.name" class="tr-jar-art" loading="lazy" decoding="async" />
                <div class="tr-jar-name">
                  {{ i.name }}
                  <span v-if="durationLabel(i.durationMs)" class="tr-jar-meta">· {{ durationLabel(i.durationMs) }}</span>
                </div>
                <div v-if="activeHint(i)" class="tr-jar-active">{{ activeHint(i) }}</div>
                <div class="tr-jar-meta">{{ magnitudeLabel(i) ?? i.description }}</div>
                <div v-if="dailyCapLabel(i)" class="tr-jar-cap">{{ dailyCapLabel(i) }}</div>
                <div class="tr-jar-foot">
                  <span class="tr-price" :class="i.currency === 'PREMIUM' ? 'text-gold-bright' : 'text-gold'">
                    <img :src="i.currency === 'PREMIUM' ? runesIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />{{ i.price.toLocaleString() }}
                  </span>
                  <button
                    :disabled="busy === i.id || !!blockedReason(i) || !affordable(i)"
                    @click="onBuySupply(i)"
                    class="btn-forge btn-forge--rune btn-forge--sm"
                    :title="blockedReason(i) ?? (!affordable(i) ? t('market.blocked.notEnough') : '')"
                  >{{ busy === i.id ? '…' : (blockedReason(i) ?? t('market.buy')) }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Golden Chest — ornate gacha card -->
        <div v-if="chestItem" class="mb-8">
          <h2 class="font-heading text-lg text-gold-bright">{{ t('market.wyrmChest.title') }}</h2>
          <p class="font-body text-xs text-text-dim mb-3">{{ t('market.wyrmChest.desc') }}</p>
          <div class="tr-chest bound-frame">
            <span class="tr-chest-wash" aria-hidden="true"></span>
            <img v-if="shopArt(chestItem.kind)" :src="shopArt(chestItem.kind)" alt="" class="tr-chest-art" />
            <div class="tr-chest-body">
              <span class="font-heading text-gold-bright text-lg leading-tight">{{ chestItem.name }}</span>
              <span class="font-body text-sm text-text-dim leading-snug">{{ chestItem.description }}</span>
            </div>
            <div class="tr-chest-buy">
              <span class="tr-price tr-price--lg tr-price--bright">
                <img :src="chestItem.currency === 'PREMIUM' ? runesIcon : cashIcon" class="w-4 h-4 shrink-0" alt="" />
                {{ chestItem.price.toLocaleString() }}
              </span>
              <button
                class="btn-forge btn-forge--rune"
                :disabled="busy === chestItem.id || !!blockedReason(chestItem) || !affordable(chestItem)"
                :title="blockedReason(chestItem) ?? (!affordable(chestItem) ? t('market.shortOnRunes') : t('market.pull'))"
                @click="onBuySupply(chestItem)"
              >{{ busy === chestItem.id ? '…' : (blockedReason(chestItem) ?? t('market.pull')) }}</button>
            </div>
          </div>
        </div>

        <!-- Stash Expansion — surfaces bagCount/bagCap so the purchase visibly matters -->
        <div v-if="expansionItem">
          <h2 class="font-heading text-lg text-gold">{{ t('market.seaChest.title') }}</h2>
          <div class="tr-expand bound-frame">
            <div class="min-w-0">
              <div class="font-heading text-text">{{ expansionItem.name }}</div>
              <div class="font-body text-xs text-text-dim">{{ expansionItem.description }}</div>
              <div v-if="slots" class="font-label uppercase tracking-wider text-[11px] text-gold mt-1">
                {{ slots.current }} / {{ slots.max }} slots
              </div>
              <div class="font-body text-[11px] text-text-dim/70 mt-0.5">
                Flat price to 100 slots · higher price up to {{ slots?.max ?? 200 }}.
              </div>
            </div>
            <div class="shrink-0 text-right">
              <template v-if="!slotsMaxed">
                <div class="tr-price tr-price--lg tr-price--bright justify-end">
                  <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />{{ expansionItem.price.toLocaleString() }}
                </div>
                <button
                  :disabled="busy === expansionItem.id || !affordable(expansionItem)"
                  @click="onBuySupply(expansionItem)"
                  class="btn-forge btn-forge--rune btn-forge--sm"
                >{{ busy === expansionItem.id ? '…' : t('market.seaChest.slots', { count: slots?.step ?? 5 }) }}</button>
              </template>
              <div v-else class="font-label uppercase tracking-wider text-[10px] text-text-dim border border-border  px-2.5 py-1.5" style="white-space:nowrap">{{ t('market.seaChest.maxed', { count: slots?.max }) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ SELL ═══════════════════════════════════════════════════ -->
      <div v-else class="grid gap-6 grid-cols-1 lg:grid-cols-[1fr_340px]">
        <div>
          <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-3">{{ t('market.sellBag.title', { count: bag.length }) }}</h2>
          <p v-if="bag.length === 0" class="font-body text-text-dim text-sm">{{ t('market.sellBag.empty') }}</p>
          <div v-else class="mkt-stall" :style="{ backgroundImage: `url(${UI.chestTray})` }">
          <SwipeDeck
            :items="bag" :page-size="6"
            min-col="160px" gap="0.85rem" :item-key="(a: any) => a.id"
          >
            <template #default="{ item: a }">
            <div class="tr-good" :class="{ 'tr-good--selected': sellTarget?.id === a.id }">
              <button
                class="relic"
                :class="{ 'relic--legend': a.rarity === 'LEGENDARY' }"
                :style="{ '--rar': RARITY_HEX[a.rarity] }"
                @click="pickToSell(a)"
              >
                <span class="relic-cord" aria-hidden="true"></span>
                <span class="relic-lv">{{ t('market.lv', { level: a.level }) }}</span>
                <img :src="itemThumb(a.type, a.rarity, a.name)" :alt="a.type" class="relic-art" loading="lazy" decoding="async" />
                <span class="relic-name">{{ a.name }}</span>
                <span class="relic-meta">{{ statLine(a) }}</span>
              </button>
            </div>
            </template>
          </SwipeDeck>
          </div>
        </div>

        <aside class="h-fit p-5 surf-plank bound-frame relative">
          <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-1">{{ t('market.sellBag.sellFrom') }}</h2>
          <p class="font-body text-text-dim text-xs mb-4">
            {{ t('market.sellBag.equippedNote') }}
          </p>

          <p v-if="!sellTarget" class="font-body text-text-dim text-sm">{{ t('market.sellBag.pickSomething') }}</p>

          <div v-else>
            <div class="mkt-sell-item">
              <img :src="itemThumb(sellTarget.type, sellTarget.rarity, sellTarget.name)" class="mkt-sell-art" alt="" />
              <div class="min-w-0">
                <div class="font-heading truncate" :style="{ color: RARITY_HEX[sellTarget.rarity] }">{{ sellTarget.name }}</div>
                <div class="font-body text-xs text-text-dim">{{ rarityLabel(sellTarget.rarity) }} · {{ t('market.lv', { level: sellTarget.level }) }}</div>
              </div>
            </div>

            <p v-if="quoteLoading" class="font-body text-text-dim text-sm">{{ t('market.sellBag.fetchingQuote') }}</p>

            <template v-else-if="sellQuote">
              <button class="mkt-sell-opt mkt-sell-opt--quick" :disabled="busy !== null" @click="onQuicksell">
                <span class="mkt-sell-opt-txt">
                  <span class="mkt-sell-opt-h">{{ t('market.quickSell') }}</span>
                  <span class="mkt-sell-opt-sub">{{ t('market.instantLose', { pct: 100 - sellQuote.quick.pct }) }}</span>
                </span>
                <span class="mkt-sell-opt-pay"><img :src="payIcon(sellQuote.currency)" class="w-4 h-4 shrink-0" alt="" />+{{ sellQuote.quick.payout.toLocaleString() }}</span>
              </button>

              <button class="mkt-sell-opt mkt-sell-opt--fence" :disabled="busy !== null" @click="onMarketSell">
                <span class="mkt-sell-opt-txt">
                  <span class="mkt-sell-opt-h">{{ t('market.brokerIt') }}</span>
                  <span class="mkt-sell-opt-sub">{{ t('market.atBroker', { time: `${sellQuote.market.lockHours}h` }) }} · {{ t('market.instantLose', { pct: 100 - sellQuote.market.pct }) }}</span>
                </span>
                <span class="mkt-sell-opt-pay mkt-sell-opt-pay--gold"><img :src="payIcon(sellQuote.currency)" class="w-4 h-4 shrink-0" alt="" />+{{ sellQuote.market.payout.toLocaleString() }}</span>
              </button>

              <p class="font-body text-[11px] text-text-dim/70 mt-2 leading-snug">
                Quick-sell pays {{ sellQuote.quick.pct }}%, right now. The broker pays {{ sellQuote.market.pct }}% — but the payout clears only after a {{ sellQuote.market.lockHours }}-hour lock.
              </p>
            </template>
          </div>

          <!-- AT THE FENCE — locked payouts, small iron tags on a cord -->
          <div v-if="pending.length" class="tr-tags-wrap">
            <div class="tr-tags-h">At the broker <span class="text-text-dim">· {{ pending.length }}</span></div>
            <div class="tr-tags">
              <div v-for="p in pending" :key="p.id" class="tr-tag">
                <span class="tr-tag-cord" aria-hidden="true"></span>
                <span class="tr-tag-name truncate" :style="{ color: RARITY_HEX[p.rarity] }">{{ p.itemName }}</span>
                <span class="tr-tag-pay"><img :src="payIcon(p.currency)" class="w-3 h-3 shrink-0" alt="" />+{{ p.payout.toLocaleString() }}</span>
                <span class="tr-tag-time">{{ untilLabel(p.matureAt) }}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </template>

    <!-- Rack item preview — view-only ItemModal w/ Buy action + compare-vs-equipped -->
    <ItemModal
      v-if="rackInspect"
      :artifact="cardToArtifact(rackInspect)"
      :slot="slotOf(rackInspect.type)"
      :is-equipped="false"
      :equipped-in-slot="equippedFor(rackInspect)"
      mode="catalog"
      :price-label="priceLabel(rackInspect.price, rackInspect.currency)"
      :buy-busy="busy === rackInspect.id"
      :blocked-reason="rackModalBlocked(rackInspect)"
      @buy="onBuyRack(rackInspect)"
      @close="rackInspect = null"
    />

    <!-- Golden Chest opening cinematic -->
    <ChestReveal v-if="chestReward" :reward="chestReward" @close="chestReward = null" />

  </section>
</template>

<style scoped>
/* ══ THE STALL FRONT — a scene, not a banner. The trader stands in it. ══ */
.tr-page { overflow: visible; }

.tr-front {
  position: relative;
  overflow: visible;
  margin-top: 40px;
  margin-bottom: 24px;
}

.tr-front-scene {
  position: relative;
  overflow: visible;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 200px;
}

.tr-front-bg {
  position: absolute; inset: 0; z-index: 0;
  width: 100%; height: 100%;
  object-fit: cover; object-position: center;
}

.tr-front-shade {
  position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background-image:
    linear-gradient(90deg, rgba(7, 12, 20, 0.25), rgba(8, 13, 22, 0.88) 72%),
    linear-gradient(0deg, rgba(6, 10, 18, 0.85), transparent 40%);
}

/* the trader — pokes above the panel on purpose; that overlap is the point */
.tr-vendor {
  position: absolute; z-index: 2;
  left: 12px; bottom: -6px;
  height: 250px; width: auto;
  filter: drop-shadow(6px 10px 18px rgba(0, 0, 0, 0.7));
  pointer-events: none;
}

.tr-front-title {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column; justify-content: center;
  padding-left: min(30%, 260px);
  padding-right: 16px;
  pointer-events: none;
}
.tr-front-h1 {
  font-family: var(--font-heading, serif);
  font-size: clamp(26px, 3vw, 36px);
  line-height: 1.12;
  margin: 0;
}
.tr-front-sub {
  font-family: var(--font-label, sans-serif);
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.07em;
  color: #b8c7d8;
  margin: 6px 0 0;
}

/* the counter — the wallet, chiselled into a small iron ledge, bottom-right */
.tr-purse {
  position: relative; z-index: 3;
  align-self: flex-end;
  display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end;
  gap: 8px;
  max-width: 100%;
  padding: 0 14px 12px;
}

@media (max-width: 700px) {
  .tr-front-scene { min-height: 170px; }
  .tr-vendor { height: 170px; left: 8px; bottom: -4px; }
  .tr-front-title { padding-left: 120px; padding-right: 10px; }
  .tr-purse { align-self: stretch; justify-content: flex-start; padding: 10px 14px 6px; }
}
/* Phone: the title was vertically centred over the WHOLE scene, so its second
   line sank behind the purse chips (owner: clipped 'Tradestead'). Anchor it to
   the top, size it to a single line, and give the scene the height the purse
   rows below actually need. (The shingle-rail phone rules live BELOW the
   rail's base rules — same specificity, so source order decides.) */
@media (max-width: 560px) {
  /* The title was centred over the WHOLE scene and the purse chips floated on
     top of it, so the second line sank behind them (owner: clipped
     'Tradestead') — and once the chips grew to the 44px tap floor they wrapped
     to three rows and buried the trader. So on a phone the scene stops being
     one overlay: the title takes a band beside the trader (its min-height
     reserves his zone), and the purse becomes a shelf BELOW the picture with
     its own scrim, where three rows of chips cost nothing. */
  .tr-front-scene { min-height: 0; }
  .tr-front-title {
    position: relative; inset: auto;
    min-height: 132px;
    justify-content: center;
    padding: 12px 12px 0 116px;
  }
  .tr-front-h1 { font-size: 20px; }
  .tr-front-sub { font-size: 9px; }
  .tr-vendor { top: 4px; bottom: auto; height: 136px; }
  .tr-purse {
    background: linear-gradient(180deg, rgba(6, 10, 18, 0.45), rgba(6, 10, 18, 0.88));
    padding: 10px 12px 12px;
  }
}

/* ── Hanging shingles — the tab rail, hung under the stall front's edge ── */
.tr-shingle-rail {
  display: flex; flex-wrap: wrap; gap: 22px;
  padding: 14px 18px 0;
  margin-bottom: 24px;
}
.tr-shingle {
  position: relative;
  isolation: isolate;
  border: 0; background: transparent; cursor: pointer;
  padding: 9px 16px 10px;
  transition: transform 130ms ease;
}
.tr-shingle-face {
  position: absolute; inset: 0; z-index: -1;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  clip-path: var(--clip-hewn);
  box-shadow: var(--rim-ice);
}
/* The hanging rods. Each must bridge the FULL gap between the stall front's
   eave and the plank, or the sign reads as floating unattached (QA 86cb1qvpt,
   twice). The rail pads 14px below the eave and the active tab sinks 3px more,
   so the rod reaches 17px up (grazing the eave in both states) and 8px INTO
   the plank; the face (z-index -1) hides that overlap, so the rod reads as
   entering the wood. */
.tr-shingle::before, .tr-shingle::after {
  content: ''; position: absolute; top: -17px; z-index: -2;
  width: 2px; height: 25px;
  background: rgba(207, 232, 255, 0.35);
}
.tr-shingle::before { left: 20%; }
.tr-shingle::after { left: 80%; }
.tr-shingle-txt {
  font-family: var(--font-label, sans-serif);
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em;
  color: #cfdae8;
}
.tr-shingle-active { transform: translateY(3px); }
.tr-shingle-active .tr-shingle-txt { color: var(--color-gold-bright, #e3c878); }
.tr-shingle-active .tr-shingle-face {
  box-shadow: var(--rim-ice), inset 0 0 0 1px color-mix(in srgb, var(--color-gold, #c8a24c) 45%, transparent);
}

.mkt-count {
  font-size: 10px; padding: 0 6px; border-radius: 0;
  background: rgba(0,0,0,0.35); color: var(--color-text-dim, #9a8f7a);
}
.tr-shingle-active .mkt-count { color: var(--color-gold, #c8a24c); background: color-mix(in srgb, var(--color-gold, #c8a24c) 18%, transparent); }

/* Phone: wrapped shingles read as a ragged stair with rods hanging from
   nothing — three tabs share ONE row instead, each an equal board. */
@media (max-width: 560px) {
  .tr-shingle-rail { flex-wrap: nowrap; gap: 10px; padding: 12px 10px 0; }
  .tr-shingle { flex: 1 1 0; min-width: 0; padding: 8px 4px 9px; text-align: center; }
  .tr-shingle-txt { font-size: 9.5px; letter-spacing: 0.03em; white-space: nowrap; }
  .tr-shingle .mkt-count { font-size: 9px; padding: 0 4px; }
}
@media (max-width: 360px) {
  .tr-shingle-txt { font-size: 9px; letter-spacing: 0.02em; }
}

/* ── Carved sidegroup label (Rack filters) ─────────────────────────────── */
.tr-filter-label {
  display: inline-flex; align-items: center;
  padding: 4px 10px;
  margin-right: 2px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-label, sans-serif);
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em;
  color: #9db0c6;
}

/* ── Filter chip / subtab embellishment (Rack rarity dot) ─────────────── */
.mkt-dot { width: 7px; height: 7px; border-radius: 0; }

/* ── The stall — a rack of iron hooks (Rack) or a fitted chest tray (Sell)
   behind the item grid. Held back hard so the item art (not the backdrop)
   is what the eye lands on first. ──────────────────────────────────────── */
.mkt-stall {
  position: relative; isolation: isolate;
  padding: 18px 14px 16px;
  background-size: cover; background-position: center;
  box-shadow: var(--rim-ice-sunken);
}
.mkt-stall::before {
  content: ''; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(8, 14, 24, 0.7), rgba(6, 11, 20, 0.84));
}
.mkt-stall > * { position: relative; z-index: 1; }

/* ── The featured shelf — the shrine, a plank shelf for the best 3 pieces ── */
/* The shrine is a WRAPPER now — SwipeDeck owns the grid inside it (and, on a
   phone, the carousel). This element keeps only the shelf: the positioning
   context, the clearance and the beam below. Because the beam hangs off THIS
   element rather than the scroll container, it stays put while the carousel
   moves — which is why the shelf survives on mobile. */
.tr-shrine {
  position: relative;
  margin-bottom: 26px;
  padding-bottom: 14px;
}
.tr-shrine::after {
  content: '';
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 0;
  height: 10px;
  background: var(--surface-beam);
  background-size: var(--surface-beam-size);
  background-blend-mode: var(--surface-beam-blend);
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.6);
}
/* descendant, not child — the deck's wrapper and page sit in between now */
.tr-shrine .tr-good { position: relative; z-index: 1; }

/* ── The good — a relic card + its price row, siblings (never nest a
   <button> inside .relic's own <button>) ──────────────────────────────── */
.tr-good { display: flex; flex-direction: column; }
/* The PLATE absorbs any extra cell height, so on a grid row shared with a
   taller (dual-priced) good the price row still sits flush under the plate
   instead of floating at the cell's bottom with dead air between (owner
   screenshot: prices detached from their cards). */
.tr-good > .relic { flex: 1 1 auto; }
.tr-good--selected { outline: 2px solid var(--color-gold, #c8a24c); outline-offset: 2px; }

.tr-price-row {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  /* auto, not a fixed 8px: two goods sharing a grid row are the same height but
     their plates are not (a longer name or a fifth stat line), so a fixed gap
     left the two BUY rows at different heights and the shelf read as ragged */
  margin-top: auto; padding: 8px 2px 0;
  /* Price + button need 123px and a two-column carousel cell on a 320px phone
     gives 115, which pushed the row past the page. Wrapping instead of a
     breakpoint keeps this self-adjusting: the button drops to its own line and
     fills it only when the two genuinely do not fit, so the wide shrine cards
     keep them side by side. */
  flex-wrap: wrap;
}
.tr-price-row > .btn-forge { flex: 1 1 auto; }
.tr-price {
  display: flex; align-items: center; gap: 4px;
  font-family: var(--font-heading, serif); font-size: 14px; font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.tr-price--bright { color: var(--color-gold-bright, #e3c878); }
.tr-price--lg { font-size: 17px; }

/* the compact stat mini-row, nested under .relic-meta */
.tr-specs { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; margin-top: 4px; }
.tr-spec { display: inline-flex; align-items: baseline; gap: 3px; }
.tr-spec b {
  font-family: var(--font-heading, serif); font-size: 12px; line-height: 1;
  color: #cfdae8; font-variant-numeric: tabular-nums;
}
.tr-spec i {
  font-style: normal;
  font-family: var(--font-label, sans-serif); font-size: 8px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #9db0c6;
}
.tr-fx { font-style: normal; color: var(--color-purple-light, #9a86ab); }

/* ── The shelf (Supplies groups) — items on a beam line ────────────────── */
.tr-shelf { position: relative; padding-bottom: 14px; margin-bottom: 6px; }
.tr-jars {
  position: relative; z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  padding-bottom: 16px;
}
.tr-shelf::after {
  content: '';
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 0;
  height: 10px;
  background: var(--surface-beam);
  background-size: var(--surface-beam-size);
  background-blend-mode: var(--surface-beam-blend);
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.6);
}
.tr-jar {
  position: relative; z-index: 1;
  isolation: isolate;
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 4px;
  padding: 12px 8px 10px;
}
/* the jar's own socket — transparent art straight on the page's photo backdrop
   read as floating clutter (owner call); same recessed face as .relic, so the
   supplies shelf and the gear tray speak one language */
.tr-jar::before {
  content: '';
  position: absolute; inset: 0; z-index: -1;
  clip-path: var(--clip-hewn);
  background:
    radial-gradient(75% 50% at 50% 28%, rgba(111, 216, 192, 0.07), transparent 75%),
    var(--tex-stone),
    linear-gradient(180deg, #121926, #0b1017 58%, #0e141d);
  background-size: auto, 150px 150px, auto;
  background-blend-mode: normal, soft-light, normal;
  box-shadow:
    inset 0 2px 5px rgba(0, 0, 0, 0.8),
    inset 0 -1px 0 rgba(226, 236, 248, 0.07),
    inset 0 0 0 1px rgba(0, 0, 0, 0.5);
}
.tr-jar-art { width: 64px; height: 64px; object-fit: contain; filter: drop-shadow(2px 6px 10px rgba(0, 0, 0, 0.6)); }
.tr-jar-name { font-family: var(--font-heading, serif); font-size: 13.5px; color: #e4ebf2; text-wrap: balance; }
/* every card reserves the same two-line description zone, so a one-line
   "+50% health" and a two-line horn blurb land their price rows on ONE line
   across the shelf instead of each card finding its own height (owner:
   crooked) — and the copy never crowds the foot below. */
.tr-jar-meta {
  font-family: var(--font-label, sans-serif); font-size: 10px; line-height: 1.5;
  letter-spacing: 0.05em; color: #aabdd2;
  min-height: calc(2 * 1.5 * 10px);
  text-wrap: balance;
}
.tr-jar-active {
  font-family: var(--font-label, sans-serif); font-size: 9px; text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--color-aurora);
}
.tr-jar-cap {
  font-family: var(--font-label, sans-serif); font-size: 9px; text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--color-gold-bright, #e3c878);
}
.tr-jar-foot {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-top: auto; padding-top: 9px; width: 100%;
  /* carved hairline seam — the foot reads as the jar's own plinth, not as a
     row the description happens to stop above */
  position: relative;
}
.tr-jar-foot::before {
  content: '';
  position: absolute; top: 0; left: 10px; right: 10px; height: 2px;
  background:
    linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)) top / 100% 1px no-repeat,
    linear-gradient(rgba(226, 236, 248, 0.09), rgba(226, 236, 248, 0.09)) bottom / 100% 1px no-repeat;
}
/* one button width across the shelf — "Buy", "Full fury" and "Full health"
   used to each cut their own size and the feet read ragged */
.tr-jar-foot .btn-forge { min-width: 104px; }

/* ── Golden Chest — bound-frame does the framing; this just lays it out ── */
.tr-chest {
  position: relative; isolation: isolate; overflow: hidden;
  display: flex; align-items: center; gap: 16px; width: 100%;
  padding: 1.1rem 1.25rem;
}
.tr-chest-wash {
  position: absolute; inset: 0; z-index: -1; pointer-events: none;
  background: linear-gradient(135deg, color-mix(in srgb, var(--color-gold, #c8a24c) 12%, transparent), transparent 62%);
  animation: legendary-pulse 2.8s ease-in-out infinite;
}
.tr-chest-art { width: 60px; height: 60px; object-fit: contain; flex: none; filter: drop-shadow(2px 6px 10px rgba(0, 0, 0, 0.55)); }
.tr-chest-body { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.tr-chest-buy { display: flex; align-items: center; gap: 16px; flex: none; }
@media (max-width: 640px) {
  .tr-chest { flex-wrap: wrap; }
  .tr-chest-body { flex-basis: calc(100% - 76px); }
  .tr-chest-buy { width: 100%; justify-content: space-between; }

}

/* ── Sea-Chest Expansion — same bound-frame, a plain row inside ───────── */
.tr-expand {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 0.9rem 1.1rem;
}

/* ── At the broker — small iron tags on a cord ─────────────────────────── */
.tr-tags-wrap {
  margin-top: 16px; padding-top: 12px;
  border-top: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
}
.tr-tags-h {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-gold); margin-bottom: 8px;
}
.tr-tags { display: flex; flex-direction: column; gap: 12px; }
.tr-tag {
  position: relative; isolation: isolate;
  display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center;
  padding: 7px 10px 6px 16px;
}
.tr-tag::before {
  content: ''; position: absolute; inset: 0; z-index: -1;
  background: var(--tex-forge, none), var(--mat-iron, var(--color-surface));
  background-size: 190px 190px, auto;
  background-blend-mode: soft-light, normal;
  clip-path: var(--clip-stele);
  box-shadow: var(--rim-ice-sunken);
}
.tr-tag-cord {
  position: absolute; top: -9px; left: 14px; z-index: -2;
  width: 2px; height: 9px;
  background: rgba(207, 232, 255, 0.35);
}
.tr-tag-name { font-family: var(--font-body, serif); font-size: 13px; min-width: 0; }
.tr-tag-pay { display: inline-flex; align-items: center; gap: 4px; color: var(--color-success); font-variant-numeric: tabular-nums; }
.tr-tag-time {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #9db0c6; min-width: 52px; text-align: right;
}

/* ── Two-tier sell aside ──────────────────────────────────────────────── */
.mkt-sell-item {
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
  padding-bottom: 12px; border-bottom: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
}
.mkt-sell-art {
  width: 44px; height: 44px; border-radius: 0; object-fit: cover; flex: none;
  border: 1px solid var(--color-border);
}
/* Two ways to sell, cut side by side into the panel. Was a pair of 8px-radius
   bordered cards; they are choices, not cards, so they use the groove grammar —
   recessed at rest, filling with light on approach. `--opt` is the light each
   one arrives with: quick-sell is a loss, so it comes up ember-hot; brokering it
   is the patient play, so it comes up aurora. */
.mkt-sell-opt {
  --opt: var(--color-aurora);
  display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%;
  padding: 11px 13px; margin-bottom: 8px;
  border: 0; border-radius: 0;
  clip-path: var(--clip-hewn);
  background: linear-gradient(180deg, #0b0f14, #12171f 60%, #0a0e13);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.8),
    inset 0 -1px 0 rgba(226, 236, 248, 0.07);
  transition: box-shadow 180ms ease, transform 90ms ease;
  text-align: left;
}
.mkt-sell-opt:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.8),
    inset 0 0 20px color-mix(in srgb, var(--opt) 22%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--opt) 55%, transparent);
}
.mkt-sell-opt:active:not(:disabled) { transform: translateY(0); }
.mkt-sell-opt:disabled { opacity: 0.5; cursor: not-allowed; }
.mkt-sell-opt--quick { --opt: var(--color-ember); }
.mkt-sell-opt--fence { --opt: var(--color-aurora); }
.mkt-sell-opt-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mkt-sell-opt-h { font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-text); }
.mkt-sell-opt-sub { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-dim); }
.mkt-sell-opt-pay {
  display: inline-flex; align-items: center; gap: 5px; flex: none;
  font-family: var(--font-heading, serif); font-size: 17px; font-weight: 700;
  color: var(--color-text); font-variant-numeric: tabular-nums;
}
.mkt-sell-opt-pay--gold { color: var(--color-gold-bright, #e3c878); }
/* silver is silver: the payout figure keeps the coin colour on both rows, since
   that number is money and money is the one thing still allowed to be gold. */

@media (prefers-reduced-motion: reduce) {
  .tr-chest-wash {
    animation: none !important;
  }
}
</style>
